/*
 * "Our story" slider on the who-we-are (Team) page: the three stories from the
 * real Czechmate home page. Slides are stacked and cross-fade on change (no
 * horizontal movement): the current slide fades out while the next fades in.
 * Prev/next arrows move one slide, it autoplays every 5s, and it loops (index
 * wraps with modulo).
 */
import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { gsap, EASE, registerGsap } from '@/animations/core'

interface Slide {
    years: string
    title: string
    summary: string
    image: string
    alt: string
}

const SLIDES: Slide[] = [
    {
        years: '23 years',
        title: 'Meet Dr. Brad Chvatal',
        summary:
            'Orthodontist, coach, marathon runner and dad of four. After 23 years running his own practice and scaling it to three locations, Brad started Czechmate to help orthodontists get clear, get unstuck and make their next bold move.',
        image: '/images/czechmate/brad-portrait.jpg',
        alt: 'Dr. Brad Chvatal smiling in a burgundy sweater against an orange wall'
    },
    {
        years: 'Opinion #3',
        title: 'Why a Nose Hair Trimmer?',
        summary:
            'A mom and her daughter saw two other orthodontists before visiting Brad. At the end of the exam, the daughter chose his practice on the spot. Her reason? The other two had nose hairs. The little things decide the game.',
        image: '/images/czechmate/nose-trimmer.jpg',
        alt: 'A blue and silver nose hair trimmer'
    },
    {
        years: 'Win!',
        title: 'The Little Word That Means Everything',
        summary:
            'We define winning by alignment: your life, your values and your practice finally moving in the same direction. That is why every client gets a Win Jar to keep score of the small victories.',
        image: '/images/czechmate/win-note.jpg',
        alt: 'A yellow sticky note with the word Win! written on it'
    }
]

export const AboutSlider: FC = (): ReactNode => {
    const slidesRef = useRef<Array<HTMLDivElement | null>>([])
    const indexRef = useRef(0)
    const animatingRef = useRef(false)
    const goRef = useRef<(dir: number) => void>(() => {})

    useEffect(() => {
        registerGsap()
        const slides = slidesRef.current
        slides.forEach((el, i) => {
            if (el) gsap.set(el, { autoAlpha: i === 0 ? 1 : 0 })
        })

        const go = (dir: number) => {
            if (animatingRef.current) return
            const n = slides.length
            const from = indexRef.current
            const to = (from + dir + n) % n
            if (to === from || !slides[from] || !slides[to]) return

            animatingRef.current = true
            gsap.to(slides[from], { autoAlpha: 0, duration: 0.5, ease: EASE })
            gsap.to(slides[to], {
                autoAlpha: 1,
                duration: 0.5,
                ease: EASE,
                onComplete: () => {
                    animatingRef.current = false
                }
            })
            indexRef.current = to
        }
        goRef.current = go

        const autoplay = window.setInterval(() => go(1), 5000)
        return () => window.clearInterval(autoplay)
    }, [])

    return (
        <div className='about-slider-container fade-in-on-scroll'>
            <div className='about-slider-viewport'>
                {SLIDES.map((slide, i) => (
                    <div
                        className='about-slide-pane'
                        key={slide.years}
                        ref={(el) => {
                            slidesRef.current[i] = el
                        }}
                    >
                        <div className='about-slide-container'>
                            <div className='about-slide-content'>
                                <div className='big-number about-slide-years'>{slide.years}</div>
                                <h2 className='about-slide-title'>{slide.title}</h2>
                                <div className='about-slide-summary-holder'>
                                    <div className='about-slide-summary'>{slide.summary}</div>
                                </div>
                            </div>
                            <div className='about-slide-image-holder'>
                                <img className='about-slide-image' src={slide.image} loading='lazy' alt={slide.alt} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className='about-slider-arrows'>
                <div
                    className='slider-arrow grey-color'
                    role='button'
                    aria-label='Previous'
                    tabIndex={0}
                    onClick={() => goRef.current(-1)}
                >
                    <span className='slider-arrow-image reverse' aria-hidden='true' />
                </div>
                <div
                    className='slider-arrow right-slider-arrow grey-color'
                    role='button'
                    aria-label='Next'
                    tabIndex={0}
                    onClick={() => goRef.current(1)}
                >
                    <span className='slider-arrow-image' aria-hidden='true' />
                </div>
            </div>
        </div>
    )
}
